import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '../lib/api';
import GateOverrideModal from './GateOverrideModal';
import { HelpPopover } from './help';

/** #3812 — the item a gate carries when its evaluation threw (apps/api/src/agents/gateItems.ts). */
export const GATE_EVALUATION_FAILED_TITLE = 'Gate evaluation could not complete';

// ── Types ──────────────────────────────────────────────────────────────────────

interface GateItem {
  title: string;
  // FT-GATE.A2 — 'WAIVED' mirrors the API's GateItem.status (phase-gate-agent.ts).
  // It is a neutral "did not apply" state (a skipped phase's prerequisite),
  // distinct from PASS/FAIL and NON-BLOCKING (the API only fails the gate on a
  // FAIL item; the failCount filter below matches 'FAIL' only).
  status: 'PASS' | 'FAIL' | 'PENDING' | 'WAIVED';
  reason?: string;
  actionRequired?: string;
  navigationUrl?: string;
  // GATE.A (#2141) — mirrors the API's GateItem.subject. A single string
  // (a prompt/story title, a file:lineRange location) or a list (e.g.
  // affected artefact types). Optional — most items have none.
  subject?: string | string[];
  /** #3898 (AVR ruling, 5 Nov 2026) — a non-blocking warning (status PASS): shown as a warning. */
  warning?: boolean;
}

/** #3898 — how an item is shown: a PASS that is a warning shows as WARN, never as "Passed". */
export function displayStatus(item: Pick<GateItem, 'status' | 'warning'>): GateItem['status'] | 'WARN' {
  return item.status === 'PASS' && item.warning === true ? 'WARN' : item.status;
}

interface GateResult {
  projectId: string;
  phase: string;
  gateType: 'ENTRY' | 'EXIT';
  overallResult: 'PASS' | 'FAIL' | 'PASS_WITH_OVERRIDE';
  items: GateItem[];
  evaluatedAt: string;
  overrideReason?: string;
  overriddenBy?: string;
  overriddenAt?: string;
}

export interface GateReportPanelProps {
  projectId: string;
  phaseNumber: number;
  gateType: 'entry' | 'exit';
  isOpen: boolean;
  onClose: () => void;
  onOverrideSuccess?: () => void;
  /** When provided, use this data instead of fetching */
  initialResult?: GateResult | null;
  /** Render inline (no backdrop / slide-over) for embedding in modals */
  inline?: boolean;
  canOverride?: boolean;
}

const PHASE_NAMES: Record<string, string> = {
  phase_1_requirements:    'Phase 1 — Requirements',
  phase_2_architecture:    'Phase 2 — Architecture',
  phase_2_5_ux_design:     'Phase 2.5 — UX Design',
  phase_3_sprint_planning: 'Phase 3 — Sprint Planning',
  phase_4_development:     'Phase 4 — Development',
  phase_5_code_review:     'Phase 5 — Code Review',
  phase_6_testing:         'Phase 6 — Testing',
  phase_7_deployment:      'Phase 7 — Deployment',
  phase_8_retrospective:   'Phase 8 — Retrospective',
};

function formatRelativeTime(dateString: string): string {
  const now = Date.now();
  const then = new Date(dateString).getTime();
  const diffMs = now - then;
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) return 'just now';
  if (diffMinutes < 60) return `${diffMinutes} minute${diffMinutes === 1 ? '' : 's'} ago`;
  if (diffHours < 24) return `${diffHours} hour${diffHours === 1 ? '' : 's'} ago`;
  if (diffDays < 30) return `${diffDays} day${diffDays === 1 ? '' : 's'} ago`;
  return new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

function statusColor(status: string) {
  // #3898 — a warning: amber, distinct from both the green PASS and the FAIL palette.
  if (status === 'WARN') return { bg: 'rgba(234,179,8,0.08)', border: 'rgba(234,179,8,0.35)', text: 'var(--warning)' };
  if (status === 'PASS') return { bg: 'rgba(16,185,129,0.06)', border: 'rgba(16,185,129,0.2)', text: 'var(--success)' };
  if (status === 'FAIL') return { bg: 'rgba(245,158,11,0.06)', border: 'rgba(245,158,11,0.2)', text: 'var(--warning)' };
  // FT-GATE.A2 — WAIVED: neutral slate, deliberately NOT the green PASS palette
  // so a skipped-phase check doesn't read as "done + passed".
  if (status === 'WAIVED') return { bg: 'rgba(100,116,139,0.08)', border: 'rgba(100,116,139,0.25)', text: 'var(--text-secondary)' };
  return { bg: 'rgba(148,163,184,0.06)', border: 'rgba(148,163,184,0.2)', text: 'var(--text-secondary)' };
}

function statusIcon(status: string) {
  if (status === 'WARN') return '⚠';
  if (status === 'PASS') return '✓';
  if (status === 'FAIL') return '✗';
  if (status === 'WAIVED') return '⊘';
  return '○';
}

function statusLabel(status: string) {
  if (status === 'WARN') return 'Warning';
  if (status === 'PASS') return 'Passed';
  if (status === 'FAIL') return 'Action required';
  if (status === 'WAIVED') return 'N/A';
  return 'Pending';
}

function overallBadge(result: string, failCount: number, warningCount = 0) {
  // #3898 — a pass with warnings says so.
  if (result === 'PASS' && warningCount > 0) return { text: 'Passed with warnings', bg: 'rgba(234,179,8,0.12)', color: 'var(--warning)', border: 'rgba(234,179,8,0.35)' };
  if (result === 'PASS') return { text: 'All checks passed', bg: 'rgba(16,185,129,0.12)', color: 'var(--success)', border: 'rgba(16,185,129,0.3)' };
  if (result === 'PASS_WITH_OVERRIDE') return { text: 'Passed with override', bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)', border: 'rgba(245,158,11,0.3)' };
  return { text: `${failCount} check${failCount !== 1 ? 's' : ''} failed`, bg: 'rgba(245,158,11,0.12)', color: 'var(--warning)', border: 'rgba(245,158,11,0.3)' };
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function GateReportPanel({
  projectId,
  phaseNumber,
  gateType,
  isOpen,
  onClose,
  onOverrideSuccess,
  initialResult,
  inline,
  canOverride,
}: GateReportPanelProps) {
  const [result, setResult] = useState<GateResult | null>(initialResult ?? null);
  const [loading, setLoading] = useState(!initialResult);
  const [overrideOpen, setOverrideOpen] = useState(false);

  const fetchResult = useCallback(() => {
    setLoading(true);
    apiFetch<GateResult>(`/projects/${projectId}/phases/${phaseNumber}/gate/${gateType}`)
      .then(data => { setResult(data); })
      .catch(() => {})
      .finally(() => { setLoading(false); });
  }, [projectId, phaseNumber, gateType]);

  useEffect(() => {
    if (initialResult || !isOpen) return;
    // Defer fetch to avoid synchronous setState in effect
    const id = setTimeout(() => fetchResult(), 0);
    return () => clearTimeout(id);
  }, [isOpen, fetchResult, initialResult]);

  // Sync initialResult changes via derived state
  const displayResult = initialResult ?? result;

  if (!isOpen) return null;

  const phaseName = displayResult ? (PHASE_NAMES[displayResult.phase] ?? displayResult.phase) : '';
  const failCount = displayResult?.items.filter(i => i.status === 'FAIL').length ?? 0;
  const failingItems = displayResult?.items.filter(i => i.status === 'FAIL') ?? [];
  // #3812 (AVR ruling 6638000744) — a gate whose evaluation did not complete offers no override: its
  // checks never ran, and the server refuses the override (503). Same title as the API's gateItems.ts.
  const evaluationIncomplete = failingItems.some(i => i.title === GATE_EVALUATION_FAILED_TITLE);
  const warningCount = displayResult?.items.filter(i => displayStatus(i) === 'WARN').length ?? 0;
  const badge = displayResult ? overallBadge(displayResult.overallResult, failCount, warningCount) : null;

  const content = (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <div style={{
        padding: inline ? 0 : '24px 24px 16px',
        borderBottom: inline ? 'none' : '1px solid var(--border)',
      }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1 }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{
                fontFamily: 'var(--font-display)', fontSize: 16, fontWeight: 700,
                color: 'var(--text)',
              }}>
                {loading ? 'Loading...' : `${phaseName} — ${gateType === 'entry' ? 'Entry' : 'Exit'} Gate Report`}
              </div>
              {/* Contextual help — explains what gate failures mean. Sits in the
                  same flex slot as the heading so it renders for every state
                  (loading, pass, fail, fail-with-override). */}
              <HelpPopover
                id="phase-gate-failure"
                heading="Understanding gate failures"
                body={
                  <>
                    <p style={{ margin: '0 0 8px' }}>
                      Phase gates check that everything required for the next phase is ready. A failure means one or more entry, task, validation, or exit criteria are unmet.
                    </p>
                    <p style={{ margin: '0 0 8px' }}>
                      Each failed criterion is listed below with what&apos;s missing. To advance, fix the listed items and re-run the gate.
                    </p>
                    <p style={{ margin: 0 }}>
                      Gate failures are not errors — they&apos;re the platform doing its job. Most are quick to resolve once the missing artefact is identified.
                    </p>
                  </>
                }
              />
              {/* Refresh button */}
              {!inline && (
                <button
                  onClick={fetchResult}
                  disabled={loading}
                  title="Refresh gate evaluation"
                  style={{
                    background: 'none', border: 'none', cursor: loading ? 'not-allowed' : 'pointer',
                    color: 'var(--text-secondary)', fontSize: 14, padding: 4, opacity: loading ? 0.5 : 1,
                  }}
                >
                  ↻
                </button>
              )}
            </div>
            {displayResult && (
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4 }}>
                Evaluated {formatRelativeTime(displayResult.evaluatedAt)}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            {badge && (
              <span style={{
                fontSize: 12, fontWeight: 600, padding: '3px 10px', borderRadius: 20,
                background: badge.bg, color: badge.color, border: `1px solid ${badge.border}`,
                whiteSpace: 'nowrap',
              }}>
                {badge.text}
              </span>
            )}
            {!inline && (
              <button
                onClick={onClose}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  fontSize: 18, color: 'var(--text-secondary)', padding: '0 2px', lineHeight: 1,
                }}
              >
                ✕
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflowY: 'auto', padding: inline ? '16px 0 0' : 24 }}>
        {loading ? (
          <div style={{
            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 40,
            color: 'var(--text-secondary)', fontFamily: 'var(--font-body)', fontSize: 14,
          }}>
            <span style={{ marginRight: 8 }}>⏳</span>
            Running pre-flight checks...
          </div>
        ) : displayResult ? (
          <>
            {/* Gate items list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {displayResult.items.map((item, i) => {
                const shown = displayStatus(item);
                const sc = statusColor(shown);
                return (
                  <div
                    key={i}
                    data-testid={`gate-item-${shown.toLowerCase()}`}
                    style={{
                      padding: '12px 14px', borderRadius: 'var(--radius-md, 8px)',
                      background: sc.bg, border: `1px solid ${sc.border}`,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
                      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 8, flex: 1 }}>
                        <span style={{
                          fontSize: 14, fontWeight: 700, flexShrink: 0, marginTop: 1,
                          color: sc.text,
                        }}>
                          {statusIcon(shown)}
                        </span>
                        <div>
                          <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>
                            {item.title}
                          </div>
                          {item.reason && (
                            <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5 }}>
                              {item.reason}
                            </div>
                          )}
                          {item.subject && (!Array.isArray(item.subject) || item.subject.length > 0) && (
                            <div
                              data-testid="gate-item-subject"
                              style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 4, lineHeight: 1.5, fontStyle: 'italic' }}
                            >
                              {Array.isArray(item.subject) ? item.subject.join(', ') : item.subject}
                            </div>
                          )}
                          {item.status === 'FAIL' && item.actionRequired && (
                            <div data-testid="action-required-text" style={{ fontSize: 12, marginTop: 4 }}>
                              {item.navigationUrl ? (
                                <Link
                                  to={item.navigationUrl}
                                  onClick={onClose}
                                  style={{ color: 'var(--primary)', textDecoration: 'underline' }}
                                  onMouseOver={e => (e.currentTarget.style.opacity = '0.8')}
                                  onMouseOut={e => (e.currentTarget.style.opacity = '1')}
                                >
                                  → {item.actionRequired}
                                </Link>
                              ) : (
                                <span style={{ color: 'var(--primary)' }}>
                                  → {item.actionRequired}
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                      <span style={{
                        fontSize: 11, fontWeight: 600, padding: '2px 10px', borderRadius: 9999,
                        background: sc.bg, color: sc.text, border: `1px solid ${sc.border}`,
                        whiteSpace: 'nowrap', flexShrink: 0,
                      }}>
                        {statusLabel(shown)}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Override section — shown for PASS_WITH_OVERRIDE */}
            {displayResult.overallResult === 'PASS_WITH_OVERRIDE' && (
              <div
                data-testid="override-section"
                style={{
                  marginTop: 16, padding: '12px 14px', borderRadius: 'var(--radius-sm)',
                  background: 'rgba(245,158,11,0.08)', border: '1px solid rgba(245,158,11,0.3)',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 14 }}>⚠</span>
                  <span style={{ fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600, color: 'var(--warning)' }}>
                    This gate was overridden
                  </span>
                </div>
                {displayResult.overrideReason && (
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)', marginLeft: 22, marginBottom: 4 }}>
                    Reason: &ldquo;{displayResult.overrideReason}&rdquo;
                  </div>
                )}
                {(displayResult.overriddenBy || displayResult.overriddenAt) && (
                  <div style={{ fontFamily: 'var(--font-body)', fontSize: 11, color: 'var(--text-secondary)', marginLeft: 22 }}>
                    {displayResult.overriddenBy && <>By: {displayResult.overriddenBy}</>}
                    {displayResult.overriddenBy && displayResult.overriddenAt && <> &middot; </>}
                    {displayResult.overriddenAt && <>At: {new Date(displayResult.overriddenAt).toLocaleString()}</>}
                  </div>
                )}
              </div>
            )}

            {/* Action buttons — shown for FAIL */}
            {displayResult.overallResult === 'FAIL' && canOverride && evaluationIncomplete && (
              <p data-testid="override-unavailable-incomplete" style={{ marginTop: 16, fontFamily: 'var(--font-body)', fontSize: 12, color: 'var(--text-secondary)' }}>
                This gate could not be evaluated just now, so it can&rsquo;t be overridden. Try again in a moment.
              </p>
            )}
            {displayResult.overallResult === 'FAIL' && canOverride && !evaluationIncomplete && (
              <button
                onClick={() => setOverrideOpen(true)}
                data-testid="override-gate-btn"
                style={{
                  marginTop: 16, width: '100%',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
                  fontFamily: 'var(--font-body)', fontSize: 13, fontWeight: 600,
                  padding: '10px 16px', borderRadius: 'var(--radius-md, 8px)',
                  border: '1px solid rgba(245,158,11,0.5)', background: 'rgba(245,158,11,0.06)',
                  color: 'var(--warning)', cursor: 'pointer',
                  transition: 'background 150ms',
                }}
                onMouseEnter={e => (e.currentTarget.style.background = 'rgba(245,158,11,0.12)')}
                onMouseLeave={e => (e.currentTarget.style.background = 'rgba(245,158,11,0.06)')}
              >
                <span style={{ fontSize: 14 }}>&#9888;</span>
                Override Gate
                <span style={{ fontSize: 12 }}>&#8599;</span>
              </button>
            )}
          </>
        ) : (
          <div style={{ padding: 20, color: 'var(--text-secondary)', fontSize: 13 }}>
            No gate evaluation available.
          </div>
        )}
      </div>

      {/* Override modal */}
      {overrideOpen && displayResult && (
        <GateOverrideModal
          projectId={projectId}
          phaseNumber={phaseNumber}
          phaseName={phaseName}
          failingItems={failingItems}
          direction={gateType === 'exit' ? 'EXIT' : 'ENTRY'}
          onClose={() => setOverrideOpen(false)}
          onOverrideSuccess={() => {
            setOverrideOpen(false);
            fetchResult();
            onOverrideSuccess?.();
          }}
        />
      )}
    </div>
  );

  // Inline mode — no backdrop
  if (inline) return content;

  // Slide-over panel from right
  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 50,
        background: 'rgba(0,0,0,0.4)',
      }}
      onClick={onClose}
    >
      <div
        style={{
          position: 'absolute', top: 0, right: 0, bottom: 0,
          width: '100%', maxWidth: 520,
          background: 'var(--surface)',
          borderLeft: '1px solid var(--border)',
          boxShadow: 'var(--shadow-lg)',
          display: 'flex', flexDirection: 'column',
        }}
        onClick={e => e.stopPropagation()}
      >
        {content}
      </div>
    </div>
  );
}
